import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, SortDescriptor, filterBy } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, Subject, combineLatest, map, startWith, switchMap, take, tap, withLatestFrom, } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IBaseSearchRequest, IFilter, IGridResult, IGridState, PageSize } from 'src/app/core/models/base';
import * as InstallationActions from "../../stores/global/installations/installation-actions";
import * as CompanyActions from "../../stores/global/companies/company-actions";
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { IGetInstallationResponse, IInstallation } from '../models/installation';
import { installationsSelector } from 'src/app/stores/global/installations/installation-selectors';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ConnectivityStateService } from 'src/app/shared/services/connectivity-state.service';
import { InstallationService } from '../services/installation.service';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';

@UntilDestroy()
@Component({
    selector: 'app-installation-view-admin',
    templateUrl: './installation-view-admin.component.html',
    styleUrl: './installation-view-admin.component.scss',
    standalone: false
})
export class InstallationViewAdminComponent implements OnInit {
  @Input() reloadResults : boolean = true;

  vm$: Observable<any>;
  keyword : string;
  showReset = false;
  selectedCategoryId : string;
  loading: boolean = false;
  filteredInstallations$: Observable<IGetInstallationResponse[]>;

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };

  constructor(
    private store: Store<AppStateInterface>,
    private fb : UntypedFormBuilder,
    private connectivityStateService : ConnectivityStateService,
    private cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
    private route: ActivatedRoute,
    private router: Router,
    private installationService: InstallationService
  ) {
    this.vm$ = combineLatest([ 
      this.gridState$.pipe(),
      this.searchRequest$.pipe(
        switchMap((request) => {
          this.loading = true;
          return this.installationService.searchInstallations(request);
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(x => {
          var gridData : GridDataResult = {
            data: x.data,
            total: x.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      ),
      this.store.pipe(select(currentEmployeeSelector)),
      this.store.pipe(select(companySelector)),
      this.route.queryParams.pipe(
        tap(params => this.patchFilterForm(params)),
        withLatestFrom(this.searchRequest$),
        map(([params, searchRequest]) => ({params, searchRequest})),
        tap(data => {
          if(JSON.stringify(data.params) !== "{}")
          {
            this.patchFilterForm(data.params);
            this.applyFilter(data.searchRequest);
          }
        }),
        take(1) // only execute once
      )
    ]).pipe(
      map(([
        gridState,
        installations,
        searchRequest,
        pageChangeSub,
        currentEmployee,
        companies,
        filterForm
      ]) => ({
        gridState,
        installations,
        searchRequest,
        pageChangeSub,
        currentEmployee,
        companies,
        filterForm
      }))
    )
  }

  installations: IGridResult<IInstallation[]>;

  // Grid Form
  public filterForm = new UntypedFormGroup({
    companyId: new UntypedFormControl(''),
    name: new UntypedFormControl('')
  });

  ngOnInit(): void {
    this.store.dispatch(CompanyActions.getCompanies());
  }

  /////////////
  // getter //
  ///////////

  get pageSizes() {
    return PageSize;
  }


  /////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();


  ////////////////
  // functions //
  //////////////



  patchFilterForm(params: any) {
    this.filterForm.patchValue({
      name: params['name'] || null,
      companyId: params['companyId'] || null,
    });
  }

  applyFilter(request: IBaseSearchRequest): void {
    var formValues = this.filterForm.value;

      const searchRequest: IBaseSearchRequest = {
        ...request,
      };
  
      // Trim the name before using
      const name = formValues.name ? formValues.name.trim() : null;
  
      // Fields to search for the name
      const searchFields = ['name'];
    
      // Update URL with the filter parameters
      const queryParams = {
        name: formValues.name || null,
        companyId: formValues.companyId || null,
      };
  
      if (checkProperties(queryParams)) {
        this.showReset = false;
      } else {
        this.showReset = true;
      }
  
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      });
  
      if (!name && !formValues.companyId) {
        this.onReset();
        return;
      }
  
      // Create keyword filters
      const nameFilters: IFilter[] = name 
          ? searchFields.map(field => ({ field, operator: 'contains', value: name })) 
          : [];
  
      // Other filters
      var filterObject : IFilter = {
        logic: "and",
        filters: []
      }
  
      // Other filters
      var otherFilters: IFilter[] =[
          { field: 'companyId', operator: 'eq', value: formValues.companyId }
      ];
  
      // remove empty filters
      otherFilters = otherFilters.filter(x => x.value != null)
  
      // Combine name filters with 'or' logic if there are any
      const combinedNameFilter: IFilter = { logic: 'or', filters: nameFilters }
  
      // Combine name filters with other filters using 'and' logic
      const allFilters: IFilter[] = combinedNameFilter.filters.length > 0
          ? [combinedNameFilter, ...otherFilters]
          : otherFilters;
  
      // Apply filters to devices
      if (allFilters.length === 0) {
          return;
      }
  
      filterObject.filters = allFilters;
  
      searchRequest.advancedFilter = filterObject;
  
      this.searchRequest$.next(searchRequest);
  }

  
  resetFilter(): void {
    // reset form
    this.filterForm.reset();
    this.showReset = false;

    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    // reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }


  onReset(){
    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    this.keyword = null;
  }
}

function checkProperties(obj: Record<string, any>): boolean {
  return Object.values(obj).every(value => value === null);
}